import { get } from 'lodash';
import { useMemo, useRef, useState } from 'react';
import { useFiltersContext } from '../../hooks';
import { FilterConfigWithValue, FilterValue } from '../../types/filter.types';
import { InternalSelect, InternalSelectProps } from './InternalSelect';
import { Chip } from '@nexelem/components';
import { CUSTOM_ICONS } from '@nexelem/icons';

// Helper function to check if filter has a meaningful value
const hasValidFilterValue = (filter: unknown): filter is { value: unknown; key?: string } =>
  !!(
    filter &&
    typeof filter === 'object' &&
    'value' in filter &&
    (filter as any).value !== '' &&
    (filter as any).value !== null &&
    (filter as any).value !== undefined
  );

type BaseChipFilterProps = Pick<
  InternalSelectProps,
  'path' | 'isMultiple' | 'register' | 'HeaderList' | 'renderOptionLabel'
> & {
  readonly label: string;
};

type MemoryChipFilterProps = BaseChipFilterProps & {
  readonly options: (FilterValue & { color?: string })[];
};

type RemoteChipFilterProps = BaseChipFilterProps & Pick<InternalSelectProps, 'fetchData' | 'getOptionId'>;

type ChipFilterProps = (MemoryChipFilterProps | RemoteChipFilterProps) & {
  readonly zIndex?: string;
  readonly className?: string;
};

export const FilterChipSelect = ({ path, isMultiple, label, className, ...props }: ChipFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  type FilterChip = FilterConfigWithValue<
    typeof isMultiple extends true ? Array<FilterValue<string | number>> : FilterValue<string | number>
  >;

  const { filters } = useFiltersContext();
  const filterValue = get(filters, path, { value: isMultiple ? [] : {} }) as FilterChip;

  const filterLabel = useMemo(() => {
    if (Array.isArray(filterValue.value)) {
      return filterValue.value.length > 0 ? `${label}: ${filterValue.value[0].key}` : label;
    }
    const filter = filterValue.value;
    return hasValidFilterValue(filter) ? `${label}: ${filter.key}` : label;
  }, [filterValue.value, label]);

  const hasSelectedValue = useMemo(() => {
    if (Array.isArray(filterValue.value)) {
      return filterValue.value.length > 0;
    }
    const filter = filterValue.value;
    return hasValidFilterValue(filter);
  }, [filterValue.value]);

  const count =
    Array.isArray(filterValue.value) && filterValue.value?.length > 1 ? `+${filterValue.value.length - 1}` : undefined;

  return (
    <>
      <Chip
        className={className}
        count={count}
        EndIcon={isOpen ? <CUSTOM_ICONS.Collapse /> : <CUSTOM_ICONS.Expand />}
        label={filterLabel}
        ref={ref}
        selected={isOpen || hasSelectedValue}
        onClick={() => setIsOpen(!isOpen)}
      />
      {ref.current && (
        <InternalSelect
          {...props}
          element={ref.current}
          isMultiple={isMultiple}
          isOpen={isOpen}
          path={path}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
};
